// Lib
import type { Types } from '@amplitude/analytics-browser';
import { forEach } from 'lodash';

// Utils
import globalLogger from '../logger/logger';
import { runIfAvailable } from './analyticsInvocationUtils';
import { getUtmData } from './utmClientUtils';
import { waitForValue } from '../../common/utils/functionUtils';
import { getDomain } from '../../common/utils/urlUtil';
import platformSingleton from '../platform/platformSingleton';
import { isPlatformDesktopPwa, isPlatformElectronMac, isPlatformIframe } from '../platform/utils/platformDetailsUtils';

// Constants
import { AMPLITUDE_USER_PROPS, TRACKED_FEATURES } from '../../common/analytics/statsConstants';
import { EVENT_TYPE_NAMES } from '../../common/analytics/amplitudeEventTypesUtil';
import { AMPLITUDE_SESSION_TIMEOUT, AMPLITUDE_START_SESSION_EVENT_TYPE } from './amplitudeSessionConstants';
import { initialiseElectronAmplitudeLifecycleTracking } from './electronAmplitudeLifecycleService';
import { applyEngagementState, createEngagementPlugin } from './engagementService';
import type { ClientConfig } from '../../common/config/configTypes';

type AmplitudeSdkModule = typeof import('@amplitude/analytics-browser');

type AmplitudeProperties = object;
type AmplitudePropertyMap = Record<string, unknown>;

type AmplitudeEvent = {
    eventProperties?: AmplitudeProperties;
    eventType?: string;
    userProperties?: AmplitudeProperties;
};

type AmplitudeInitialisationConfig = {
    analytics?: Pick<ClientConfig['analytics'], 'amplitudeApiKey' | 'amplitudeEnabled'>;
    appVersion?: ClientConfig['appVersion'];
};

const asValidPropertyType = (value: unknown): Types.ValidPropertyType => value as Types.ValidPropertyType;

let hasInitialisedAmplitude = false;
let isAmplitudeTrackingEnabled = true;

// Calls wait in order for the lazily loaded SDK. A null SDK drains them without sending after failure.
type SdkCallback = (loadedSdk: AmplitudeSdkModule | null) => void;
let amplitudeSdk: AmplitudeSdkModule | null = null;
let hasSdkLoadSettled = false;
const pendingSdkCallbacks: SdkCallback[] = [];

/**
 * Runs a callback against the loaded SDK, preserving call order while it loads.
 */
const withSdk = (callback: SdkCallback): void => {
    if (!isAmplitudeTrackingEnabled) return;

    if (hasSdkLoadSettled) {
        callback(amplitudeSdk);
        return;
    }

    pendingSdkCallbacks.push(callback);
};

const settleSdkLoad = (loadedSdk: AmplitudeSdkModule | null): void => {
    amplitudeSdk = loadedSdk;
    hasSdkLoadSettled = true;
    pendingSdkCallbacks.splice(0).forEach((callback) => callback(loadedSdk));
};

let sdkModulePromise: Promise<AmplitudeSdkModule> | null = null;

/**
 * Loads and caches the SDK on first use.
 */
const loadSdk = (): Promise<AmplitudeSdkModule> => {
    if (!sdkModulePromise) {
        sdkModulePromise = import(/* webpackChunkName: "amplitude-sdk" */ '@amplitude/analytics-browser');
    }

    return sdkModulePromise;
};

const isAmplitudeTrackingAvailable = () => hasInitialisedAmplitude;
const runIfAmplitudeTrackingIsAvailable = runIfAvailable('Amplitude', isAmplitudeTrackingAvailable);

const logger = globalLogger.createChannel('amplitude-service');

/**
 * Sends properties in one identify call, optionally recording initial or appended values.
 */
const identifyProperties = (
    loadedSdk: AmplitudeSdkModule,
    entries: Record<string, unknown>,
    { setInitial = false, appendKeys = [] }: { setInitial?: boolean; appendKeys?: string[] } = {},
): void => {
    const identify = new loadedSdk.Identify();

    forEach(entries, (value, key) => {
        if (appendKeys.includes(key)) {
            identify.append(key, asValidPropertyType(value));
            return;
        }

        identify.set(key, asValidPropertyType(value));
        if (setInitial) identify.setOnce(`initial_${key}`, asValidPropertyType(value));
    });

    loadedSdk.identify(identify);
};

/**
 * Tracks the start-session event whenever the SDK assigns a new session id.
 */
const createSessionStartPlugin = (loadedSdk: AmplitudeSdkModule): Types.EnrichmentPlugin => ({
    name: 'milanote-session-start',
    type: 'enrichment',
    onSessionIdChanged: (sessionId) => {
        loadedSdk.track(AMPLITUDE_START_SESSION_EVENT_TYPE, undefined, { session_id: sessionId });
        return Promise.resolve();
    },
});

/**
 * Gets the Amplitude device identifier when browser analytics is available.
 */
export const getAmplitudeDeviceId = runIfAmplitudeTrackingIsAvailable(() => {
    return amplitudeSdk?.getDeviceId();
});

/**
 * Waits for initialisation before returning the Amplitude device identifier.
 */
export const waitForAmplitudeDeviceId = (): Promise<string | undefined> => {
    if (!hasInitialisedAmplitude || (hasSdkLoadSettled && !amplitudeSdk)) return Promise.resolve(undefined);
    return waitForValue(getAmplitudeDeviceId);
};

export const enableAmplitudeTracking = (enable: boolean): Promise<void> => {
    isAmplitudeTrackingEnabled = enable;
    if (!enable && !hasSdkLoadSettled) pendingSdkCallbacks.splice(0);
    if (!hasInitialisedAmplitude || !amplitudeSdk) return Promise.resolve();

    try {
        amplitudeSdk.setOptOut(!enable);

        if (enable) {
            // Sensitive data audit: SAFE — Static success message with no user data
            logger.info('%cAmplitude analytics successfully enabled', 'color: green');
        }
    } catch (error: unknown) {
        // Sensitive data audit: SAFE — warn auto-sanitized (static message, error object)
        logger.warn('Failed to enable Amplitude tracking', error);
    }

    return Promise.resolve();
};

/**
 * Resets the Amplitude session id.
 */
export const resetAmplitudeSession = runIfAmplitudeTrackingIsAvailable(() => {
    withSdk((loadedSdk) => {
        loadedSdk?.setSessionId(Date.now());
    });
});

/**
 * Associates browser analytics with the current user.
 */
export const setAmplitudeUser = runIfAmplitudeTrackingIsAvailable((user: { _id: string }) => {
    withSdk((loadedSdk) => {
        loadedSdk?.setIdentity({ userId: user._id, userProperties: {} });
    });
});

/**
 * Removes the current browser analytics user association.
 */
export const clearAmplitudeUser = runIfAmplitudeTrackingIsAvailable(() => {
    withSdk((loadedSdk) => {
        loadedSdk?.setIdentity({ userId: undefined, userProperties: {} });
    });
});

/**
 * Sends browser analytics user properties and the current application platform.
 */
export const sendAmplitudeUserProperties = runIfAmplitudeTrackingIsAvailable(
    ({ userProperties = {} }: Pick<AmplitudeEvent, 'userProperties'>) => {
        withSdk((loadedSdk) => {
            if (!loadedSdk) return;

            const amplitudeUserProperties: AmplitudePropertyMap = { ...userProperties };
            const exampleBoards = amplitudeUserProperties[AMPLITUDE_USER_PROPS.EXAMPLE_BOARDS];
            if (exampleBoards) {
                identifyProperties(
                    loadedSdk,
                    { [AMPLITUDE_USER_PROPS.EXAMPLE_BOARDS]: exampleBoards },
                    { appendKeys: [AMPLITUDE_USER_PROPS.EXAMPLE_BOARDS] },
                );
            }

            delete amplitudeUserProperties[AMPLITUDE_USER_PROPS.EXAMPLE_BOARDS];
            amplitudeUserProperties[AMPLITUDE_USER_PROPS.APPLICATION_PLATFORM] = platformSingleton.applicationPlatform;

            identifyProperties(loadedSdk, amplitudeUserProperties);
        });
    },
);

/**
 * Sends an event to Amplitude.
 */
export const sendAmplitudeEvent = runIfAmplitudeTrackingIsAvailable(
    ({ eventType, eventProperties, userProperties }: AmplitudeEvent) => {
        sendAmplitudeUserProperties({ userProperties });

        if (!eventType) return;

        withSdk((loadedSdk) => {
            loadedSdk?.track(eventType, eventProperties ? { ...eventProperties } : undefined);
        });
    },
);

const URL_ATTRIBUTION_PARAM_GROUPS = [
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'],
    ['gclid'],
];

/**
 * Identifies the present parameters in a URL attribution group.
 */
const identifyUrlParamGroup = (loadedSdk: AmplitudeSdkModule, keys: string[]): void => {
    const params = new URLSearchParams(document.location.search);
    const entries: Record<string, unknown> = {};

    keys.forEach((key) => {
        const value = params.get(key);
        if (value !== null) entries[key] = value;
    });

    if (Object.keys(entries).length === 0) return;

    identifyProperties(loadedSdk, entries, { setInitial: true });
};

/**
 * Identifies URL attribution groups and the raw referrer when present.
 */
const identifyUrlAttributionParams = (loadedSdk: AmplitudeSdkModule): void => {
    URL_ATTRIBUTION_PARAM_GROUPS.forEach((keys) => identifyUrlParamGroup(loadedSdk, keys));

    if (!document.referrer) return;

    identifyProperties(
        loadedSdk,
        {
            [AMPLITUDE_USER_PROPS.REFERRAL_URL]: document.referrer,
            [AMPLITUDE_USER_PROPS.REFERRAL_DOMAIN]: getDomain(document.referrer),
        },
        { setInitial: true },
    );
};

/**
 * Identifies derived UTM attribution data.
 */
const identifyUtmData = runIfAmplitudeTrackingIsAvailable(() => {
    const utmData = getUtmData(document.location.href, document.referrer);
    if (!utmData) return;

    withSdk((loadedSdk) => {
        if (!loadedSdk) return;
        identifyProperties(loadedSdk, { ...utmData }, { setInitial: true });
    });
});

/**
 * Registers Engagement without making Analytics availability depend on its remote bundle.
 */
const registerEngagementPlugin = async (loadedSdk: AmplitudeSdkModule): Promise<void> => {
    try {
        await loadedSdk.add(createEngagementPlugin()).promise;
        await applyEngagementState();
    } catch (error: unknown) {
        // Sensitive data audit: SAFE — warn auto-sanitized (static message, error object)
        logger.warn('Failed to initialise Amplitude Engagement', error);
    }
};

export const initialiseAmplitude = async (config: AmplitudeInitialisationConfig): Promise<void> => {
    if (hasInitialisedAmplitude) return;
    if (!config.analytics?.amplitudeEnabled) return;

    const amplitudeApiKey = config.analytics?.amplitudeApiKey;
    if (!amplitudeApiKey) return;

    hasInitialisedAmplitude = true;

    const deviceId = new URLSearchParams(document.location.search).get('amp_device_id') ?? undefined;
    const amplitudeConfig: Types.BrowserOptions = {
        appVersion: config.appVersion,
        deviceId,

        // Sends only explicitly tracked events.
        autocapture: false,

        // Keeps the shared device identifier cookie available to the marketing site.
        cookieOptions: {
            upgrade: false,
        },

        // Disables unused remote configuration requests.
        remoteConfig: {
            fetchRemoteConfig: false,
        },

        // Allows requests to complete while the page exits.
        transport: 'beacon',

        sessionTimeout: AMPLITUDE_SESSION_TIMEOUT,
        trackingOptions: {
            ipAddress: false,
        },
    };

    let loadedSdk: AmplitudeSdkModule;
    try {
        loadedSdk = await loadSdk();
        amplitudeSdk = loadedSdk;
        loadedSdk.add(createSessionStartPlugin(loadedSdk));
        const initResult = loadedSdk.init(amplitudeApiKey, undefined, {
            ...amplitudeConfig,
            optOut: !isAmplitudeTrackingEnabled,
        });

        // The destination plugin is registered inside init()'s promise; events sent before it
        // resolves are dropped, so nothing is issued until it has.
        await initResult.promise;
        loadedSdk.setOptOut(!isAmplitudeTrackingEnabled);
        // Sensitive data audit: SAFE — Static success message with no user data
        logger.info('%cAmplitude analytics successfully loaded', 'color: green');
    } catch (error: unknown) {
        // Sensitive data audit: SAFE — warn auto-sanitized (static message, error object)
        logger.warn('Failed to initialise Amplitude', error);
        settleSdkLoad(null);
        return;
    }

    if (isPlatformElectronMac(platformSingleton)) initialiseElectronAmplitudeLifecycleTracking(resetAmplitudeSession);

    // URL attribution and the application platform are identified before any call queued during
    // loading runs, so a later UTM fallback cannot overwrite the URL's own values.
    identifyUrlAttributionParams(loadedSdk);
    identifyProperties(loadedSdk, {
        [AMPLITUDE_USER_PROPS.APPLICATION_PLATFORM]: platformSingleton.applicationPlatform,
    });

    // Beacons survive unload, so flushing on page hide delivers events tracked just before leaving.
    window.addEventListener('pagehide', () => {
        void loadedSdk.flush().promise.catch((error: unknown) => {
            // Sensitive data audit: SAFE — warn auto-sanitized (static message, error object)
            logger.warn('Failed to flush Amplitude on page hide', error);
        });
    });

    settleSdkLoad(loadedSdk);
    void registerEngagementPlugin(loadedSdk);
};

/**
 * Adds platform-specific browser analytics data after initialisation.
 */
export const initialiseAmplitudeData = runIfAmplitudeTrackingIsAvailable(() => {
    if (isPlatformIframe(platformSingleton)) {
        sendAmplitudeEvent({
            eventType: EVENT_TYPE_NAMES.LOADED_IN_IFRAME,
            eventProperties: {
                referrer: document.referrer,
            },
        });
    }

    if (isPlatformElectronMac(platformSingleton)) {
        sendAmplitudeUserProperties({
            userProperties: {
                [AMPLITUDE_USER_PROPS.FEATURE]: { [TRACKED_FEATURES.DESKTOP_APP]: true },
            },
        });
    } else if (isPlatformDesktopPwa(platformSingleton)) {
        sendAmplitudeUserProperties({
            userProperties: {
                [AMPLITUDE_USER_PROPS.FEATURE]: { [TRACKED_FEATURES.PWA]: true },
            },
        });
    } else {
        identifyUtmData();
    }
});
